import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-object-lookup',
  templateUrl: './object-lookup.component.html',
  styleUrls: ['./object-lookup.component.scss']
})
export class ObjectLookupComponent implements OnChanges {
  @Input() id: number | null = null;
  @Input() label: string;
  @Output() idChange = new EventEmitter<number | null>();

  public searchTerm = '';
  public searchResults: any[] = [];
  public selectedObject: any = null;
  public isLoadingObject = false;
  public isSearching = false;

  private searchTerm$ = new BehaviorSubject<string>('');

  constructor() {
    this.searchTerm$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap(searchText => this.searchObjects(searchText)),
      catchError(() => of([]))
    ).subscribe(results => {
      this.searchResults = results || [];
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.id && !changes.id.firstChange) {
      this.loadObjectById();
    }

    if (changes.objectType && !changes.objectType.firstChange) {
      this.clearSelection();
    }

    if (changes.id && changes.id.firstChange) {
      this.loadObjectById();
    }
  }

  public getObjectType(): string {
    return '';
  }

  public onSearch(text: string) {
    this.searchTerm = text;
    this.isSearching = true;

    if (this.selectedObject && this.getObjectId(this.selectedObject) !== this.id) {
      this.selectedObject = null;
    }

    if (!text?.trim()) {
      this.searchResults = [];
      return;
    }

    this.searchTerm$.next(text.trim());
  }

  public selectObject(item: any) {
    this.selectedObject = item;
    this.searchTerm = this.getObjectName(item);
    this.searchResults = [];
    this.isSearching = false;
    this.emitIdChange(this.getObjectId(item));
  }

  public enterSearchMode() {
    this.isSearching = true;
    this.searchResults = [];

    // If there's already a selected object, immediately use its name to drive the search
    // so the user sees matching results right away.
    const existingTerm = this.searchTerm?.trim();
    if (existingTerm) {
      this.onSearch(existingTerm);
      return;
    }

    if (this.selectedObject) {
      const name = this.getObjectName(this.selectedObject);
      if (name) {
        this.searchTerm = name;
        this.onSearch(this.searchTerm);
      }
    }
  }

  private emitIdChange(value: number | null) {
    this.idChange.emit(value);
  }

  protected loadObjectById() {
    if (this.id == null) {
      // No id -> enter search mode so user can pick an object
      this.selectedObject = null;
      this.searchResults = [];
      this.searchTerm = '';
      this.isSearching = true;
      return;
    }

    this.isLoadingObject = true;
    this.loadObjectByIdFromService(this.id);
  }

  protected loadObjectByIdFromService(id: number) {
    // Override in subclass
    this.isLoadingObject = false;
    this.clearSelection();
  }

  protected searchObjects(searchText: string): Observable<any[]> {
    // Override in subclass
    return of([]);
  }

  protected clearSelection() {
    this.selectedObject = null;
    this.searchResults = [];
    this.searchTerm = '';
    this.isSearching = false;
  }

  public getObjectName(item: any): string {
    if (!item) {
      return '';
    }
    return item.Name || item.name || item.DisplayName || item.Label || '';
  }

  public getObjectId(item: any): number | null {
    if (!item) {
      return null;
    }
    return item.ID ?? item.id ?? null;
  }
}
